import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Table, type TableProps } from 'antd';
import { type ColumnType } from 'antd/es/table';
import clx from 'classnames';
import { type CSSProperties, useMemo } from 'react';
import { down } from 'styled-breakpoints';
import styled, { useTheme } from 'styled-components';
import { ResponsiveCard } from '../card/ResponsiveCard.tsx';
import { ExpandedRow } from './column-picker/expanded/ExpandedRow.tsx';
import { tableLoaderSpin } from './tableLoaderSpin.tsx';

export const PaginationInfoWrapper = styled.span`
  color: ${props => props.theme.colorText};
  margin-right: 6px;
`;

// Personalizzazione della visualizzazione dell'ordinamento della tabella (header)
// const TableHeaderCellStyle = css`
//   .ant-table {
//     .ant-table-thead {
//       th.ant-table-cell {
//         &.ant-table-column-sort {
//           /*  background: rgba(0, 0, 0, 0.08);
//           background: ${props => props.theme.controlItemBgActiveHover};  */
//         }
//         &.sort-cell-overwrite {
//           &.ant-table-column-has-sorters.ant-table-column-sort {
//             .ant-table-column-sorter {
//               /* display: none; */
//             }
//           }
//         }
//       }
//     }
//   }
// `;

const CardTableWrapper = styled.div`
  /* Stile Dark della barra del titolo */
  .dark-title-bar {
    .ant-table-title {
      background-color: ${props => props.theme.tableTitleBackgroundColor};
      .ant-typography,
      span,
      p {
        color: ${props => props.theme.colorBgContainer};
        font-weight: 400;
        margin: 0;
      }
    }
  }

  .title-bar-no-padding .ant-table {
    .ant-table-title {
      padding: 0;
      ${down('sm')} {
        padding: 0;
      }
    }
  }

  // Rimozione della shadow della Card
  &.remove-shadow .responsive-card {
    box-shadow: none;
  }

  // intestazioni tabella tutte maiuscolo
  .uppercase-thead {
    .ant-table-thead {
      > tr > th {
        text-transform: uppercase;
        letter-spacing: 0.05em;
        font-size: ${props => props.theme.fontSizeSM}px;
        line-height: 1.2em;
      }
    }
  }

  .ant-table {
    line-height: 1.3em;
  }

  // se ci sono colonne fisse
  .ant-table {
    .ant-table-cell-fix-left {
      /* overflow-x: hidden; */
    }
  }

  /* Importiamo gli stili per TableDataHeaderSortCell da applicare alla Tabella */
  /* TableHeaderCellStyle */

  .ant-table {
    .ant-table-title {
      padding: 8px 16px;
      ${down('sm')} {
        padding: 8px;
      }

      /* background-color: ${props => props.theme.tableTitleBackgroundColor};
      .ant-typography,
      span,
      p {
        color: ${props => props.theme.colorBgContainer};
        font-weight: 400;
        margin: 0;
      } */
    }
    /*  schiariamo la direzione non selezionata per evidenziare l'altra */
    .ant-table-column-sorter-up,
    .ant-table-column-sorter-down {
      &:not(.active) {
        color: rgba(0, 0, 0, 0.15);
      }
    }
    /*  mettiamo in evidenza la riga filtrata */
    .ant-table-filter-trigger {
      color: rgba(0, 0, 0, 0.66);
      &:hover {
        color: rgba(0, 0, 0, 0.88);
      }
      &.active {
        background-color: ${props => props.theme.colorPrimaryBg};
        &:hover {
          background-color: ${props => props.theme.colorPrimaryBg};
        }
      }
    }
    .ant-table-thead,
    .ant-table-tbody {
      > tr {
        > td,
        > th {
          ${down('sm')} {
            padding: 12px 8px;
          }
        }
      }
    }

    // Accessibilità colore testo nessun dato
    .ant-table-tbody {
      .ant-empty {
        .ant-empty-description {
          color: ${props => props.theme.colorTextSecondary};
        }
      }
      .ant-table-expanded-row.expanded-row-white-background {
        .ant-table-cell {
          background-color: white;
          padding: 12px 16px;
        }
      }
    }

    // accessibilità header della tabella - outline su elemento corrente
    .ant-table-thead {
      th {
        &:focus-visible {
          .ant-table-column-sorters {
            outline: ${props => props.theme.lineWidthFocus}px solid
              ${props => props.theme.focusOutlineColor} !important;
            outline-offset: 1px;
            border-radius: ${props => props.theme.borderRadius}px;
          }
        }
      }
    }
  }
  .ant-table-pagination.ant-pagination {
    margin-left: 16px;
    margin-right: 16px;
    ${down('sm')} {
      margin-left: 8px;
      margin-right: 8px;
    }
  }

  // scroll verticale con intestazioni sticky (via CSS, senza scroll.y di Ant Design)
  &.table-scrollable {
    .ant-table-content {
      max-height: var(--table-scrollable-height);
      overflow-y: auto;
    }
    .ant-table-thead > tr > th {
      position: sticky;
      top: 0;
      z-index: 3;
    }
    .ant-table-thead > tr > th.ant-table-cell-fix-left,
    .ant-table-thead > tr > th.ant-table-cell-fix-right {
      z-index: 4;
    }
  }

  // gestione dello scroll orizzontale della tabella
  // solo se non ci sono colonne fisse
  &.table-without-fixed-columns {
    .table-data-responsive-card {
      .ant-card-body {
        // sposto lo scroll orizzontale sul table-container
        // per evitare che includa Titolo Tabella e Paginazione
        // -- considerando che nel Titolo includiamo anche la Ricerca Avanzata
        .ant-table .ant-table-container{
          max-width: 100%;
          overflow-x: auto;
        }
      }
    }
  }
`;

export interface TableDataProps<T>
  extends Omit<TableProps<T>, 'loading' | 'columns' | 'dataSource'> {
  loading?: boolean;
  dataSource?: T[];
  afterTable?: React.ReactNode;
  columns?: Array<ColumnType<T>>;
  /** Barra del titolo scura (di default) */
  darkTitleBar?: boolean;
  /** Rimozione della shadow */
  removeShadow?: boolean;
  /** Intestazioni tabella tutte maiuscole */
  uppercaseThead?: boolean;
  /** Rimozione del padding dal titolo */
  titleBarNoPadding?: boolean;
  /**
   * Altezza massima del body della tabella (es. `'400px'`, `'50vh'`).
   * Attiva scroll verticale e intestazioni sticky.
   */
  scrollableHeight?: string;
}
/**
 * Tabella con stili e paginazione personalizzati
 */
export function TableData<T extends object>(props: TableDataProps<T>) {
  const {
    loading,
    pagination,
    columns: inputColumns,
    darkTitleBar = true,
    removeShadow,
    uppercaseThead,
    titleBarNoPadding = true,
    scrollableHeight,
    ...otherProps
  } = props;

  const theme = useTheme();

  const paginationBase: TableProps<T>['pagination'] =
    pagination === false
      ? false
      : {
          position: ['bottomRight'],
          size: 'small',
          showSizeChanger: true,
          showTotal: (total, range) => (
            <PaginationInfoWrapper>
              {range[0]}-{range[1]} di {total}
            </PaginationInfoWrapper>
          ),
          prevIcon: <LeftOutlined aria-label="precedente" />,
          nextIcon: <RightOutlined aria-label="successivo" />,
          ...pagination,
        };

  const expandedColumns = useMemo(
    () => inputColumns?.filter(c => c.showInExpandedRow && !c.hidden) ?? [],
    [inputColumns],
  );

  const columns = useMemo(
    () => inputColumns?.filter(c => !c.showInExpandedRow) ?? [],
    [inputColumns],
  );

  const hasFixedColumns = useMemo(() => {
    const hasActionColumn =
      columns.some(
        c => (c.fixed == 'right' || c.fixed == 'left') && !c.showInExpandedRow,
      ) || otherProps.rowSelection?.fixed != null;

    /**
     * In alcune configurazioni specifiche, dobbiamo attivare lo scroll _solo_
     * se appaiono le colonne dei Tag Semantici.
     * In altre, lo scroll è attivo a prescindere dalle colonne.
     */
    const shouldActivateScroll =
      !theme.tableScrollRequiresColumnFlag ||
      columns.some(c => c.columnFlags?.includes('enableScroll'));

    return hasActionColumn && shouldActivateScroll;
  }, [columns, otherProps.rowSelection, theme.tableScrollRequiresColumnFlag]);

  const scrollConfig = useMemo(
    () => (hasFixedColumns ? { x: 'max-content' as const } : undefined),
    [hasFixedColumns],
  );

  // Applichiamo la larghezza specificata in `widthWithActiveScroll`
  // alle colonne, nel caso lo scroll orizzontale sia attivo
  const columnsWithWidth = useMemo(() => {
    return columns.map(col => {
      if (hasFixedColumns && col.widthWithActiveScroll) {
        return {
          ...col,
          width: col.widthWithActiveScroll,
        };
      }
      return col;
    });
  }, [columns, hasFixedColumns]);

  return (
    <CardTableWrapper
      className={clx({
        'remove-shadow': removeShadow,
        'table-without-fixed-columns': !hasFixedColumns,
        'table-scrollable': !!scrollableHeight,
      })}
      style={
        scrollableHeight
          ? ({ '--table-scrollable-height': scrollableHeight } as CSSProperties)
          : undefined
      }
    >
      <ResponsiveCard noPadding className="table-data-responsive-card">
        <Table<T>
          loading={tableLoaderSpin(loading)}
          pagination={paginationBase}
          rowKey="id"
          columns={columnsWithWidth}
          expandable={
            expandedColumns.length > 0
              ? {
                  expandedRowRender: record => (
                    <ExpandedRow
                      data={record}
                      columns={expandedColumns as Array<ColumnType<T>>}
                    />
                  ),
                }
              : undefined
          }
          scroll={scrollConfig}
          {...otherProps}
          className={clx(otherProps.className, {
            'dark-title-bar': darkTitleBar,
            'uppercase-thead': uppercaseThead,
            'title-bar-no-padding': titleBarNoPadding,
          })}
        />
        {props.afterTable}
      </ResponsiveCard>
    </CardTableWrapper>
  );
}
